<template>
  <div class="diet-list">
    <div class="search">
      <BaseInputSearch
        test-id="DietListGroups"
        :placeholder="'Wyszukaj szablony po nazwie lub opisie...'"
        :debounce-after="750"
        :model-value="searchPhrase"
        @update:model-value="filterDiets($event)"
      />
    </div>
    <div class="nav-actions">
      <DietListNav :diet-type="dietType" :diets-count="dietsCount" />
      <div class="actions">
        <Space direction="columns" :space="6">
          <Space direction="columns" :space="0">
            <div class="move-diet-wrapper">
              <Tooltip placement="top">
                <template #trigger>
                  <IconButton
                    name="folder"
                    :disabled="moveDisabled || selectedDietsEmpty || loading"
                    @click.stop="showMoveDietMenu"
                  />
                </template>
                Przenieś zaznaczone szablony do wybranej grupy<br>
                <template v-if="moveDisabled">
                  <br>
                  <span class="tooltip-bold">
                    Maksymalna dozwolona liczba zaznaczonych jednocześnie szablonów to
                    {{ dietsMoveLimit }}
                    .
                  </span>
                  <br>
                  <span class="tooltip-bold">
                    Zaznaczono {{ selectedDietsCount }}
                    . Zmniejsz liczbę zaznaczonych szablonów.
                  </span>
                </template>
              </Tooltip>
              <DietListCopyMoveMenu
                v-if="moveDietMenu.visible"
                class="move-diet-menu"
                :title="'Przenieś do:'"
                :groups="groupsOptions"
                @select-group="moveDiet"
                @add-group-click="hideMoveDietMenu(), showAddGroupModal()"
                @close="hideMoveDietMenu()"
              />
            </div>
            <Tooltip v-if="canAssignToLeader" placement="top">
              <template #trigger>
                <IconButton
                  name="school"
                  :disabled="assignToLeaderDisabled || selectedDietsEmpty || loading"
                  @click="showAssignToLeaderModal()"
                />
              </template>
              Przypisz zaznaczone szablony do prowadzącego
              <template v-if="shareDisabled">
                <br>
                <br>
                <span class="tooltip-bold">
                  Maksymalna dozwolona liczba zaznaczonych jednocześnie szablonów to
                  {{ dietsAssignToLeaderLimit }}
                  .
                </span>
                <br>
                <span class="tooltip-bold">
                  Zaznaczono {{ selectedDietsCount }}
                  . Zmniejsz liczbę zaznaczonych szablonów.
                </span>
              </template>
            </Tooltip>
            <Tooltip placement="top">
              <template #trigger>
                <IconButton
                  name="share"
                  :disabled="shareDisabled || selectedDietsEmpty || loading"
                  @click="handleShowShareDietModal()"
                />
              </template>
              Udostępnij zaznaczone szablony innemu użytkownikowi
              <template v-if="shareDisabled">
                <br>
                <br>
                <span class="tooltip-bold">
                  Maksymalna dozwolona liczba zaznaczonych jednocześnie szablonów to
                  {{ dietsShareLimit }}
                  .
                </span>
                <br>
                <span class="tooltip-bold">
                  Zaznaczono {{ selectedDietsCount }}
                  . Zmniejsz liczbę zaznaczonych szablonów.
                </span>
              </template>
            </Tooltip>
            <Tooltip placement="top">
              <template #trigger>
                <IconButton
                  name="delete"
                  type="destructive"
                  variant="outline"
                  :disabled="selectedDietsEmpty || loading"
                  @click="showDeleteDietModal()"
                />
              </template>
              Usuń zaznaczone szablony
            </Tooltip>
          </Space>
          <StyledButton
            left-icon="add"
            variant="secondary"
            :disabled="loading"
            @click="showAddGroupModal"
          >
            Dodaj grupę
          </StyledButton>
          <StyledButton
            v-if="!isUniversityDiet"
            left-icon="add"
            :disabled="loading"
            @click="showAddDietModal"
          >
            Dodaj szablon
          </StyledButton>
        </Space>
      </div>
    </div>
    <div class="list">
      <div class="header row">
        <div class="column" />
        <div class="column">
          <BaseCheckbox :model-value="selected.all" @update:model-value="handleAllCheckbox()" />
        </div>
        <div class="header__group column" @click="setSortByGroups()">
          <div class="column__sortable">
            Nazwa grupy
            <BaseIcon :name="sortByGroups === 'name' ? 'expand_less' : 'expand_more'" />
          </div>
        </div>
        <div class="header__name column">
          <div class="column__sortable" @click="setSortByDiets('name')">
            Nazwa szablonu
            <BaseIcon :name="getIconBySort('name')" />
          </div>
        </div>
        <div class="header__description column">
          <div class="column__sortable" @click="setSortByDiets('description')">
            Opis
            <BaseIcon :name="getIconBySort('description')" />
          </div>
        </div>
        <div class="column">
          <div class="column__sortable" @click="setSortByDiets('created')">
            Data utworzenia
            <BaseIcon :name="getIconBySort('created')" />
          </div>
        </div>
        <div class="column">
          <div class="column__sortable" @click="setSortByDiets('calories')">
            Kaloryczność (kcal/dzień)
            <BaseIcon :name="getIconBySort('calories')" />
          </div>
        </div>
        <div v-if="loading" class="list-loader-bar">
          <div class="list-loader-bar__progress" />
        </div>
      </div>
      <div v-if="groups.length > 0 || searchPhrase === ''" class="groups">
        <div
          v-for="group in groups"
          :key="`group-${group.id}`"
          class="group-wrapper"
          @drop="handleDietDrop(group, $event)"
          @dragover.prevent
          @dragenter.prevent="setHoverGroupOrDiet(`group-${group.id}`)"
        >
          <div v-if="group.id === 0" class="group-zero-info">
            <BaseIcon type="info" name="info" />
            Poniższe szablony nie znajdują się w żadnej ze stworzonych przez Ciebie grup. Dodaj je
            do grup istniejących bądź utwórz nową, aby ułatwić sobie pracę.
          </div>
          <div
            class="group row"
            :class="{
              'group--selected': group.selected && groupIsSelectable(group),
              'group--hovered': isHoveredGroupOrDiet(`group-${group.id}`)
            }"
            @mouseenter="setHoverGroupOrDiet(`group-${group.id}`)"
            @mouseleave="setHoverGroupOrDiet('')"
          >
            <div class="column">
              <IconButton
                class="group__expand"
                :name="group.expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                @click="expandGroup(group)"
              />
            </div>
            <div class="column">
              <BaseCheckbox
                :partial="groupPartiallySelected(group)"
                :disabled="!groupIsSelectable(group)"
                :model-value="group.selected && groupIsSelectable(group)"
                @update:model-value="handleGroupCheckbox(group)"
              />
            </div>
            <div class="column group__name">
              <span>
                {{ group.name }}
                <span class="group__count">({{ group.dietsCount }})</span>
              </span>
            </div>
            <div class="column" />
            <div class="column" />
            <div class="column" />
            <div class="column" />
            <div
              v-if="isHoveredGroupOrDiet(`group-${group.id}`) && group.id !== 0"
              class="row__actions"
            >
              <div class="group__actions">
                <Tooltip placement="top-end">
                  <template #trigger>
                    <IconButton name="edit" @click.stop="showGroupNameEdit(group)" />
                  </template>
                  Edytuj nazwę
                </Tooltip>
                <Tooltip placement="top-end">
                  <template #trigger>
                    <IconButton
                      type="destructive"
                      name="delete"
                      variant="outline"
                      @click="showDeleteGroupModal(group)"
                    />
                  </template>
                  Usuń grupę i przenieś<br>
                  szablony do katalogu głównego
                </Tooltip>
              </div>
            </div>
            <div v-if="group.id === groupNameEdit.id" class="group__name-edit" @click.stop>
              <input
                ref="groupNameInput"
                v-model="groupNameEdit.name"
                class="group__name-edit__input"
                :data-test-id="`GroupNameInput__${group.id}__${group.name}`"
                maxlength="50"
                @keyup.enter="saveGroupName"
                @keyup.esc="hideGroupNameEdit"
              >
              <IconButton
                name="save"
                :disabled="groupNameEdit.name.length === 0"
                @click="saveGroupName"
              />
            </div>
          </div>
          <div v-if="group.expanded" class="copy-diet-wrapper">
            <div
              class="diets-wrapper"
              :class="{
                'diets-wrapper--scroll': group.diets.length > 10,
                'diets--copy-active': copyDietMenu.visible
              }"
              @scroll="throttledDietScroll(group, $event)"
            >
              <div v-if="group.diets.length > 0" class="diets">
                <div v-if="loadingDiets" class="diets-loader" />
                <div
                  v-for="(diet, dietIndex) in group.diets"
                  :key="`group-${group.id}-diet-${diet.id}`"
                  class="diet row"
                  :class="{
                    'diet--selected': diet.selected || isHighlightedDiet(`diet-${diet.id}`)
                  }"
                  @mouseenter="setHoverGroupOrDiet(`diet-${diet.id}`)"
                  @mouseleave="setHoverGroupOrDiet('')"
                >
                  <div class="column" />
                  <div class="column">
                    <BaseCheckbox
                      :model-value="diet.selected"
                      @update:model-value="handleDietCheckbox(diet, group)"
                    />
                  </div>
                  <div class="column" />
                  <div class="column diet__name">
                    <div v-if="diet.id === dietNameEdit.id" class="diet__name-edit" @click.stop>
                      <input
                        ref="dietNameInput"
                        v-model="dietNameEdit.name"
                        :data-test-id="`DietNameInput__${diet.id}__${diet.name}`"
                        class="diet__name-edit__input"
                        maxlength="85"
                        @keyup.enter="saveDietName"
                        @keyup.esc="hideDietNameEdit"
                      >
                      <IconButton
                        name="save"
                        size="small"
                        icon-size="16"
                        :disabled="dietNameEdit.name.length === 0"
                        @click="saveDietName"
                      />
                    </div>
                    <router-link
                      v-else
                      class="diet__name-link"
                      :to="{ name: 'DietDetail', params: { dietType, dietId: diet.id } }"
                      :draggable="'true'"
                      @dragstart="handleDietDragStart(diet, group, $event)"
                      @dragend="handleDragEnd"
                    >
                      {{ diet.name }}
                    </router-link>
                  </div>
                  <div class="column diet__description">
                    <span :title="diet.description">{{ diet.description }}</span>
                  </div>
                  <div class="column diet__created">{{ diet.created }}</div>
                  <div class="column diet__calories">{{ diet.calories }}</div>
                  <div v-if="isHoveredGroupOrDiet(`diet-${diet.id}`)" class="row__actions">
                    <div class="diet__actions">
                      <Tooltip
                        :placement="
                          dietIndex !== 0 || group.diets.length <= 10 ? 'top-end' : 'bottom-end'
                        "
                      >
                        <template #trigger>
                          <IconButton name="edit" @click.stop="showDietNameEdit(diet)" />
                        </template>
                        Edytuj nazwę
                      </Tooltip>
                      <Tooltip
                        :placement="
                          dietIndex !== 0 || group.diets.length <= 10 ? 'top-end' : 'bottom-end'
                        "
                      >
                        <template #trigger>
                          <IconButton
                            name="filter_none"
                            icon-size="18"
                            @click.stop="showCopyDietMenu(diet.id)"
                          />
                        </template>
                        Skopiuj szablon
                      </Tooltip>
                      <Tooltip
                        v-if="dietType !== 'university'"
                        :placement="
                          dietIndex !== 0 || group.diets.length <= 10 ? 'top-end' : 'bottom-end'
                        "
                      >
                        <template #trigger>
                          <IconButton
                            name="person_add"
                            @click.stop="showCopyToPatientModal(diet.id)"
                          />
                        </template>
                        Skopiuj do pacjenta
                      </Tooltip>
                      <Tooltip
                        v-if="canAssignToLeader"
                        :placement="
                          dietIndex !== 0 || group.diets.length <= 10 ? 'top-end' : 'bottom-end'
                        "
                      >
                        <template #trigger>
                          <IconButton
                            name="school"
                            @click.stop="showAssignToLeaderModal(diet.id)"
                          />
                        </template>
                        Przypisz szablon do prowadzącego
                      </Tooltip>
                      <Tooltip
                        :placement="
                          dietIndex !== 0 || group.diets.length <= 10 ? 'top-end' : 'bottom-end'
                        "
                      >
                        <template #trigger>
                          <IconButton name="share" @click="handleShowShareDietModal(diet.id)" />
                        </template>
                        Udostępnij szablon innemu użytkownikowi
                      </Tooltip>
                      <Tooltip
                        :placement="
                          dietIndex !== 0 || group.diets.length <= 10 ? 'top-end' : 'bottom-end'
                        "
                      >
                        <template #trigger>
                          <IconButton
                            variant="outline"
                            type="destructive"
                            name="delete"
                            @click="showDeleteDietModal(diet)"
                          />
                        </template>
                        Usuń szablon
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="diets-empty">
                <div class="diets-empty__info">
                  Twoja grupa <span class="diets-empty__info-name">{{ group.name }}</span> jest
                  pusta.<br>
                  <template v-if="isUniversityDiet">
                    Dodaj do niej szablony przeciągając istniejące już szablony z innych grup.
                  </template>
                  <template v-else-if="group.name === 'Udostępnione'">
                    W tej grupie będą znajdowały się szablony udostępnione Ci przez innych
                    dietetyków.
                  </template>
                  <template v-else>
                    Dodaj do niej szablony klikając w przycisk powyżej
                    <span class="diets-empty__info-button">+ Dodaj szablon</span>
                    lub przeciągnij istniejące już szablony z innych grup.
                  </template>
                </div>
              </div>
            </div>
            <DietListCopyMoveMenu
              v-if="copyDietMenu.visible"
              class="copy-diet-menu"
              :title="'Skopiuj do:'"
              :groups="groupsOptions"
              @select-group="copyDiet"
              @add-group-click="hideCopyDietMenu(), showAddGroupModal()"
              @close="hideCopyDietMenu()"
            />
          </div>
        </div>
      </div>
      <div v-else class="groups-empty">
        <BaseIcon name="info" type="info" />
        <span>Nie znaleziono żadnych szablonów.</span>
      </div>
      <div v-if="loading" class="list-loader" />
    </div>
    <AddDietModal v-if="addDietModal.visible" :diet-type="dietType" />

    <BaseModal v-if="deleteDietModal.visible" @close="hideDeleteDietModal" @confirm="deleteDiet">
      <template v-if="deleteDietModal.diet" #title>Usuwanie szablonu</template>
      <template v-else #title>Usuwanie szablonów</template>
      <div v-if="deleteDietModal.diet" class="diet-delete-modal-info">
        Czy chcesz usunąć szablon o nazwie
        <span class="modal-bold">{{ deleteDietModal.diet.name }}</span>
        ?
      </div>
      <div v-else>
        <template v-if="selected.diets.size === 1">
          Zaznaczono <span class="modal-bold">1 szablon</span>
          . Czy na pewno chcesz go usunąć?
        </template>
        <template v-else>
          Zaznaczono <span class="modal-bold">{{ selected.diets.size }} szablony</span>
          . Czy na pewno chcesz je usunąć?
        </template>
      </div>
      <template #actions>
        <StyledButton variant="outline" @click.once="hideDeleteDietModal">Anuluj</StyledButton>
        <StyledButton @click.once="deleteDiet">Usuń</StyledButton>
      </template>
    </BaseModal>

    <BaseModal
      v-if="addGroupModal.visible"
      :modal-class="'add-group-modal'"
      @close="hideAddGroupModal"
      @confirm="addGroup"
    >
      <template #title>Dodawanie grupy</template>
      <div class="modal-label">Wprowadź nazwę grupy</div>
      <BaseInput
        v-model="addGroupModal.name"
        test-id="AddGroupModal__Name"
        class="add-group-modal__input"
        :maxlength="50"
        :placeholder="'Twoja nazwa grupy'"
        :force-focus="true"
      />
      <template #actions>
        <StyledButton variant="outline" @click.once="hideAddGroupModal">Anuluj</StyledButton>
        <StyledButton :disabled="addGroupButtonDisabled" @click.once="addGroup">
          Dodaj grupę
        </StyledButton>
      </template>
    </BaseModal>

    <BaseModal v-if="deleteGroupModal.visible" @close="hideDeleteGroupModal" @confirm="deleteGroup">
      <template #title>Usuwanie grupy</template>
      <div class="group-delete-modal-info">
        Czy na pewno chcesz usunąć grupę
        <span class="modal-bold">{{ deleteGroupModal?.group?.name ?? '' }}</span>
        ?<br>
        Jej zawartość zostanie przeniesiona do katalogu głównego.
      </div>
      <template #actions>
        <StyledButton variant="outline" @click.once="hideDeleteGroupModal">Anuluj</StyledButton>
        <StyledButton @click.once="deleteGroup">Usuń</StyledButton>
      </template>
    </BaseModal>

    <DietCopyToPatientModal
      v-if="copyToPatientModal.visible"
      :diet-type="dietType"
      :diet-id="copyToPatientModal.dietId"
      @close="hideCopyToPatientModal"
    />

    <DietAssignToLeaderModal
      v-if="assignToLeaderModal.visible"
      :diet-type="dietType"
      :diets-ids="assignToLeaderModal.dietsIds"
      @close="hideAssignToLeaderModal"
    />

    <DietShareModal
      v-if="shareDietModal.visible"
      :diet-type="dietType"
      :diets-ids="shareDietModal.dietsIds"
      @close="hideShareDietModal"
    />

    <div id="ghost-image" class="ghost-image">
      <BaseIcon class="ghost-image__icon" name="assignment" />
      {{ ghostText() }}
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { PropType } from 'vue'
  import { computed } from 'vue'

  import BaseCheckbox from '@/components/BaseCheckbox.vue'
  import BaseIcon from '@/components/BaseIcon.vue'
  import BaseInput from '@/components/BaseInput.vue'
  import BaseInputSearch from '@/components/BaseInputSearch.vue'
  import BaseModal from '@/components/BaseModal.vue'
  import DietAssignToLeaderModal from '@/components/diets/DietAssignToLeaderModal.vue'
  import DietCopyToPatientModal from '@/components/diets/DietCopyToPatientModal.vue'
  import DietListCopyMoveMenu from '@/components/diets/DietListCopyMoveMenu.vue'
  import DietListNav from '@/components/diets/DietListNav.vue'
  import DietShareModal from '@/components/diets/DietShareModal.vue'
  import { IconButton } from '@/components/IconButton'
  import { Space } from '@/components/Space'
  import { StyledButton } from '@/components/StyledButton'
  import { Tooltip } from '@/components/ui'
  import { pushDataLayer } from '@/utils/pushDataLayer'

  import type { DietType } from '@/store/types'

  import AddDietModal from './AddDietModal/AddDietModal.vue'
  import { useDietListGroupsProvider } from './providers/dietListGroupsProvider'

  const props = defineProps({
    dietType: {
      required: true,
      type: String as PropType<DietType>,
    },
  })

  const dietType = computed(() => props.dietType)

  const {
    addGroup,
    addGroupButtonDisabled,
    addGroupModal,
    assignToLeaderDisabled,
    assignToLeaderModal,
    copyToPatientModal,
    canAssignToLeader,
    copyDiet,
    copyDietMenu,
    deleteDiet,
    deleteDietModal,
    deleteGroup,
    deleteGroupModal,
    dietNameEdit,
    dietsAssignToLeaderLimit,
    dietsCount,
    dietsMoveLimit,
    dietsShareLimit,
    moveDiet,
    moveDietMenu,
    filterDiets,
    hideDietNameEdit,
    ghostText,
    groupIsSelectable,
    groupNameEdit,
    groups,
    groupsOptions,
    handleAllCheckbox,
    handleDietCheckbox,
    handleDietDragStart,
    handleDietDrop,
    showAddDietModal,
    showDeleteDietModal,
    hideDeleteDietModal,
    showAddGroupModal,
    hideAddGroupModal,
    showDeleteGroupModal,
    hideDeleteGroupModal,
    showDietNameEdit,
    saveDietName,
    showGroupNameEdit,
    saveGroupName,
    handleDragEnd,
    isUniversityDiet,
    selectedDietsEmpty,
    moveDisabled,
    shareDisabled,
    groupPartiallySelected,
    setSortByDiets,
    setSortByGroups,
    expandGroup,
    setHoverGroupOrDiet,
    isHoveredGroupOrDiet,
    isHighlightedDiet,
    handleGroupCheckbox,
    showAssignToLeaderModal,
    hideAssignToLeaderModal,
    showCopyToPatientModal,
    hideCopyToPatientModal,
    showShareDietModal,
    hideShareDietModal,
    showCopyDietMenu,
    hideCopyDietMenu,
    showMoveDietMenu,
    hideMoveDietMenu,
    selected,
    shareDietModal,
    searchPhrase,
    loading,
    getIconBySort,
    sortByGroups,
    loadingDiets,
    selectedDietsCount,
    hideGroupNameEdit,
    throttledDietScroll,
    addDietModal,
  } = useDietListGroupsProvider()

  const handleShowShareDietModal = (dietId?: number) => {
    pushDataLayer({
      event: 'diet_share_click',
    })
    showShareDietModal(dietId)
  }
</script>

<style scoped>
  .diet-list {
    min-width: 1050px;
    margin-bottom: 24px;
  }

  .search {
    position: sticky;
    top: 0;
    z-index: 150;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px;
    margin-bottom: 24px;
    background: #fff;
    box-shadow: 2px 4px 4px 0 rgb(232 232 232 / 50%);
  }

  .nav-actions {
    position: sticky;
    top: 68px;
    z-index: 150;
    display: flex;
    background: #f8f8f8;
  }

  .actions {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .add-group,
  .add-diet {
    margin-left: 16px;
  }

  .list {
    position: relative;
    padding: 0 12px 12px;
    font-size: 12px;
    background: #fff;
  }

  .list-loader {
    position: absolute;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: top;
    justify-content: center;
    cursor: wait;
    background: rgb(255 255 255 / 40%);
  }

  .list-loader-bar {
    position: absolute;
    inset: 52px 0 0;
    z-index: 200;
    width: 100%;
    height: 2px;
    overflow: hidden;
    background-color: transparent;
    border-radius: 9999px;
  }

  .list-loader-bar__progress {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 50%;
    background-color: var(--color-main-100);
    border-radius: 9999px;
    animation-name: progress-bar;
    animation-duration: 3s;
    animation-iteration-count: infinite;
  }

  @keyframes progress-bar {
    from {
      left: -50%;
    }

    to {
      left: 100%;
    }
  }

  .row {
    display: grid;
    grid-template-columns: 28px 12px 150px minmax(165px, auto) 180px 150px 180px;
    column-gap: 10px;
  }

  .row__actions {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    display: none;
  }

  .column {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .column__sortable {
    display: flex;
    align-items: center;
    height: 52px;
    cursor: pointer;
  }

  .column__sortable .base-icon {
    cursor: pointer;
  }

  .header {
    position: sticky;
    top: 112px;
    z-index: 100;
    height: 52px;
    margin-bottom: 12px;
    font-family: Montserrat-Medium;
    color: var(--color-main-100);
    background: #fff;
    border-bottom: 2px solid #eaeaea;
  }

  .header__group,
  .header__name,
  .header__description {
    justify-content: left;
  }

  .groups-empty {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 20px 0;
  }

  .group-zero-info {
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 20px 0;
  }

  .group {
    position: relative;
    height: 32px;
    margin-bottom: 6px;
    background: #f5f5f5;
  }

  .group--selected {
    background: #eaeaea;
  }

  .group:hover {
    background: var(--color-main-10);
  }

  .group--hovered {
    background: var(--color-main-10);
  }

  .group__expand {
    position: relative;
    left: 5px;
  }

  .group__name {
    justify-content: left;
    width: 600px;
    font-family: Montserrat-Medium;
  }

  .group__count {
    color: var(--color-secondary-text);
  }

  .group__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 32px;
    padding: 0 10px 0 50px;
    background: linear-gradient(to right, transparent, var(--color-main-10) 50px);
  }

  .group__name-edit {
    position: absolute;
    inset: 0;
    z-index: 50;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    background: #f5f5f5;
    border-bottom: 2px solid var(--color-main-100);
  }

  .group__name-edit__input {
    width: 600px;
    padding: 0;
    margin-left: 60px;
    font-family: Montserrat-Medium;
    outline: 0;
    background: transparent;
    border: 0;
  }

  .group__name-edit__button {
    margin-right: 20px;
  }

  .group:hover .row__actions {
    display: block;
  }

  .diets-wrapper {
    position: relative;
    max-height: 374px;
    margin-bottom: 6px;
  }

  .diets-wrapper--scroll {
    overflow: hidden scroll;
  }

  .diets--copy-active {
    /* overflow-y: visible;
                            overflow-x: visible; */
  }

  .diets-empty__info {
    padding: 19px 0;
    margin: 10px 0;
    font-family: Montserrat-Medium;
    line-height: 1.4;
    text-align: center;
    border: 2px dashed var(--color-main-100);
  }

  .diets-empty__info-name {
    font-family: Montserrat-Bold;
  }

  .diets-empty__info-button {
    font-family: Montserrat-Bold;
    color: var(--color-main-100);
  }

  .diets {
    position: relative;
  }

  .diets-loader {
    position: absolute;
    inset: 0;
    z-index: 10000;
    cursor: wait;
    background: rgb(255 255 255 / 60%);
  }

  .diet {
    position: relative;
    height: 32px;
    margin-bottom: 6px;
  }

  .diet:hover,
  .diet--selected {
    background: var(--color-main-10);
  }

  .diet__name {
    position: relative;
    justify-content: left;
  }

  .diet__name-link {
    display: block;
    width: 100%;
    height: 32px;
    margin-left: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 32px;
    color: var(--color-primary-text);
    white-space: nowrap;
    text-decoration: none;
  }

  .diet__name-edit {
    position: absolute;
    inset: 1px 0;
    z-index: 50;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    background: transparent;
    border-bottom: 2px solid var(--color-main-100);
  }

  .diet__name-edit__input {
    width: calc(100% - 30px);
    padding: 0;
    margin-left: 8px;
    outline: 0;
    background: transparent;
    border: 0;
  }

  .diet__name-edit__button {
    margin-right: 8px;
  }

  .diet__description {
    justify-content: flex-start;
  }

  .diets-wrapper--scroll .diet__description {
    margin-left: 10px;
  }

  .diet__description span {
    display: inline-block;
    align-items: flex-start;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 16px;
    white-space: nowrap;
  }

  .diet__created {
    justify-content: flex-start;
    margin-left: 45px;
  }

  .diet__calories {
    justify-content: flex-end;
    margin-right: 75px;
  }

  .diet__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 32px;
    padding: 0 10px 0 50px;
    background: linear-gradient(to right, transparent, var(--color-main-10) 50px);
  }

  .diet:hover .row__actions {
    display: block;
  }

  .move-diet-wrapper {
    position: relative;
  }

  .move-diet-menu {
    top: 12px;
    right: 12px;
  }

  .copy-diet-wrapper {
    position: relative;
  }

  .copy-diet-menu {
    top: 6px;
    right: 22px;
  }

  .modal-bold {
    font-family: Montserrat-SemiBold;
  }

  .modal-label {
    margin-left: 8px;
    font-size: 10px;
    color: var(--color-tertiary-text);
  }

  .add-group-modal__input {
    width: 100%;
  }

  .add-diet-modal__input {
    width: 100%;
    margin-bottom: 20px;
  }

  .diet-delete-modal-info {
    overflow-wrap: break-word;
  }

  .group-delete-modal-info {
    overflow-wrap: break-word;
  }

  .ghost-image {
    position: absolute;
    top: -500px;
    display: flex;
    align-items: center;
    padding: 6px;
    font-family: Montserrat-SemiBold;
    font-size: 12px;
    color: #fff;
    background-color: #5dbab3;
    border-radius: 5px;
  }

  .ghost-image__icon {
    padding-right: 6px;
    color: #fff;
  }

  .tooltip-bold {
    font-weight: bold;
  }
</style>

<style>
  .add-diet-modal {
    overflow: visible !important;
  }
</style>
